/* cc-search — client-side instant search over cc-search-index.json.
   Accent-folded, case-insensitive, matches anywhere in a word
   (mid-word included). Scoring: exact word > word start > substring.
   Without JS the form submits to Shopify's /search page.

   v2 (Tony 2026-07-11, Nike-inspired):
   - Idle focus shows the server-rendered idle panel (popular searches +
     quick links, cloned from the snippet's <template>).
   - Typing shows Top Suggestions (matched part bolded) + BIG product
     cards (index images swapped to width=360).
   - Header context expands into a fixed page overlay. The dropdown is
     reparented to <body>: the frosted header's backdrop-filter is a
     containing block for fixed descendants (CONVENTIONS). */

class CCSearch extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.cc-search__form');
    this.input = this.querySelector('.cc-search__input');
    this.dropdown = this.querySelector('.cc-search__dropdown');
    this.idleTpl = this.querySelector('.cc-search__idle-tpl');
    if (!this.input) return;

    this.isHeader = this.classList.contains('cc-search--header');
    if (this.isHeader && this.dropdown) {
      this.dropdown.classList.add('cc-search__dropdown--overlay');
      document.body.appendChild(this.dropdown); // element refs survive the move
    }

    // clear (×) button — visible whenever there's a query
    this.clearBtn = this.querySelector('.cc-search__clear');
    if (this.clearBtn) {
      this.clearBtn.hidden = !this.input.value;
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.clearBtn.hidden = true;
        this.openIdle(); // back to suggestions, not a dead end
        this.input.focus();
      });
    }

    this.cancelBtn = this.querySelector('.cc-search__cancel');
    if (this.cancelBtn) {
      this.cancelBtn.addEventListener('click', () => {
        this.close();
        this.input.blur(); // drop the keyboard too
      });
    }

    const engage = () => {
      this.loadIndex();
      if (this.input.value.trim().length < 2) this.openIdle();
      else if (this.dropdown.hidden) this.query();
    };
    this.input.addEventListener('focus', engage);
    // a tap on an ALREADY-focused input fires no focus event (close via
    // scroll/X keeps focus) — the bar looked dead on the next tap
    this.input.addEventListener('click', () => {
      if (this.dropdown.hidden) engage();
    });

    // header overlay: a real scroll closes the panel (iOS scrolls through
    // the body lock). Armed 400ms after open with a re-anchored baseline —
    // entering compact mode reflows the page and fires a synthetic scroll
    // event that was closing the panel the instant it opened.
    window.addEventListener(
      'scroll',
      () => {
        if (!this.isHeader || this.dropdown.hidden) return;
        // phones: the body is position:fixed while open, so any scroll
        // event is iOS keyboard pan, NOT the user leaving — it was
        // closing the sheet the moment the pan settled past the grace
        // window (Tony: "search isn't showing up at all sometimes")
        if (window.matchMedia('(max-width: 749px)').matches) return;
        if (Date.now() - (this.openT || 0) < 400) {
          this.openY = window.scrollY; // reflow settling — re-anchor
          return;
        }
        if (Math.abs(window.scrollY - (this.openY || 0)) > 60) this.close();
      },
      { passive: true }
    );
    this.input.addEventListener('input', () => {
      if (this.clearBtn) this.clearBtn.hidden = !this.input.value;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.query(), 120);
    });
    this.input.addEventListener('keydown', (e) => this.onKey(e));
    // selection is native: double-click = word, triple-click = all
    // (a select-all-on-dblclick override shipped briefly and was
    // reverted — Tony 2026-07-11)

    // chips + suggestions run a query without leaving the panel
    this.dropdown.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-cc-term]');
      if (!chip) return;
      this.input.value = chip.dataset.ccTerm;
      if (this.clearBtn) this.clearBtn.hidden = false;
      this.input.focus();
      this.query();
    });

    document.addEventListener('click', (e) => {
      // chips are re-rendered away mid-click: a detached target read as
      // "outside" and closed the panel (Tony: suggestion clicks closed it)
      if (!e.target.isConnected) return;
      // iOS: opening reflows the header stack, then Safari delivers the
      // tap's CLICK at the ORIGINAL finger position — it lands on the
      // dim/moved page, read as "outside", and closed the sheet the
      // instant it opened (Tony: tap does nothing). Grace window.
      if (this._openedAt && Date.now() - this._openedAt < 600) return;
      if (!this.contains(e.target) && !(this.dropdown && this.dropdown.contains(e.target))) this.close();
    });

    if (this.classList.contains('cc-search--hero')) this.typewriter();
  }

  /* Hero placeholder types itself out — a small living touch.
     Focus (or reduced motion) short-circuits to the full text. */
  typewriter() {
    const full = this.input.getAttribute('placeholder');
    if (!full || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let stopped = false;
    const stop = () => {
      stopped = true;
      this.input.placeholder = full;
    };
    this.input.addEventListener('focus', stop, { once: true });
    this.input.placeholder = '';
    let i = 0;
    const tick = () => {
      if (stopped) return;
      i += 1;
      this.input.placeholder = full.slice(0, i);
      if (i < full.length) setTimeout(tick, 38 + Math.random() * 45);
    };
    setTimeout(tick, 500);
  }

  loadIndex() {
    if (this.indexPromise) return this.indexPromise;
    /* v3 (2026-07-19): the index ships SPLIT — a small manifest
       (generated/pop/collections/articles + `chunks` list) plus N
       product chunk files beside it. One ~115KB file exceeded the
       per-write payload ceiling of the scheduled cloud task that
       regenerates it, so the regen writes several small assets
       instead. Chunk fetches cache-bust on the manifest's `generated`
       stamp. A legacy single-file index (no `chunks`) loads unchanged. */
    this.indexPromise = fetch(this.dataset.indexUrl)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data.chunks) || !data.chunks.length) return (this.index = data);
        const base = this.dataset.indexUrl.split('?')[0].replace(/[^/]+$/, '');
        const bust = '?gen=' + encodeURIComponent(data.generated || '');
        return Promise.all(
          data.chunks.map((f) => fetch(base + f + bust).then((r) => r.json()))
        ).then((parts) => {
          data.products = parts.flatMap((p) => p.products || []);
          delete data.chunks;
          return (this.index = data);
        });
      })
      .catch(() => (this.indexPromise = null));
    return this.indexPromise;
  }

  /* Blog language context (2026-07-19, Tony): cc:lang memory (set by
     explicit cc-lang-link/switcher clicks) -> es. Weglot hostname ->
     default en. Article entries carry tw (twin path) when an ES<->EN
     twin exists; the wrong-language half of a PAIR is hidden. Untwinned
     articles always show. Keep in sync w/ cc-lang-switcher.js (KEY). */
  static lang() {
    let l = null;
    try {
      l = localStorage.getItem('cc:lang');
    } catch (e) {
      /* private mode */
    }
    if (l !== 'es' && l !== 'en') l = location.hostname.indexOf('es.') === 0 ? 'es' : 'en';
    return l;
  }

  static artLang(h) {
    return h.indexOf('sabores-chilenos/') === 0 ? 'es' : 'en';
  }

  static fold(str) {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  score(entry, tokens) {
    let total = 0;
    for (const token of tokens) {
      const s = entry.s;
      let best = 0;
      let pos = s.indexOf(token);
      while (pos !== -1) {
        const atWordStart = pos === 0 || s[pos - 1] === ' ';
        const atWordEnd = pos + token.length === s.length || s[pos + token.length] === ' ';
        const hit = atWordStart && atWordEnd ? 3 : atWordStart ? 2 : 1;
        if (hit > best) best = hit;
        if (best === 3) break;
        pos = s.indexOf(token, pos + 1);
      }
      if (best === 0) return 0; // every token must match somewhere
      total += best;
    }
    /* Title-priority tie-breakers (Tony 2026-07-14): whole-word hits score
       identically for "Sahne-Nuss" / "Sahne-Nuss Bitter" / "... Pasa el
       Ron" and raw index order decided the tie. Exact title > title
       starts with the query > all tokens somewhere in the title (vs
       matching only via tags/variants). */
    const ft = CCSearch.fold(entry.t);
    const q = tokens.join(' ');
    if (ft === q) total += 3;
    else if (ft.startsWith(q)) total += 2;
    else if (tokens.every((tk) => ft.includes(tk))) total += 1;
    /* final tie-break: shorter title = closer match ("Chocolate
       Sahne-Nuss" beats "Chocolate Sahne-Nuss (Bitter)") - fractional,
       never outweighs a real scoring difference */
    total -= Math.min(ft.length, 200) / 1000;
    return total;
  }

  /* phones: overflow:hidden is advisory on iOS — the page still pans
     under the sheet. Pin the body fixed at the recorded offset instead;
     close() restores the scroll position. */
  lockBody() {
    if (window.matchMedia('(max-width: 749px)').matches && this._lockY == null) {
      this._lockY = window.scrollY;
      document.body.style.setProperty('--cc-lock-top', -this._lockY + 'px');
    }
    document.body.classList.add('cc-search-lock');
  }

  /* shared open: shows the panel, measures the overlay top (header) */
  open() {
    // The open menu drawer sits at body level (z90) and covered the
    // results sheet below the header line -- with the menu up, tapping
    // the docked search stacked two takeovers and the dropdown rendered
    // "cut off" at the drawer's top edge (Tony 2026-07-22, phone
    // screenshot). Search owns the screen: close the drawer. opener is
    // nulled first -- drawer.close() refocuses its opener (the burger)
    // 250ms later, which would steal focus off this input mid-type and
    // drop the keyboard.
    const drawer = document.querySelector('cc-menu-drawer');
    if (drawer && !drawer.hidden && typeof drawer.close === 'function') {
      drawer.opener = null;
      drawer.close();
    }
    if (this.isHeader) {
      document.body.classList.remove('cc-hdr-hidden'); // header stays put while the panel is up
      this.openY = window.scrollY;
      this.openT = Date.now();
      const phone = window.matchMedia('(max-width: 749px)').matches;
      // mobile: compact search mode — logo bar + trophy row collapse so
      // the bar + results own the screen (keyboard eats the rest)
      if (window.matchMedia('(max-width: 989px)').matches) {
        document.body.classList.add('cc-search-compact');
        void document.body.offsetHeight; /* reflow before measuring */
      }
      const measure = () => {
        if (this.dropdown.hidden) return;
        // phones: body is position:fixed while open, so any document
        // scroll is iOS keyboard pan — zero it or the sticky header
        // (with the bar in it) pans off-screen and a gap opens between
        // the sheet and the top (Tony's typing screenshot)
        if (phone && window.scrollY) window.scrollTo(0, 0);
        const vv = window.visualViewport;
        this.dropdown.style.setProperty('--cc-vv-h', Math.round(vv ? vv.height : window.innerHeight) + 'px');
        const dock = document.querySelector('.cc-header-dock');
        const wrapper = document.querySelector('.header-wrapper');
        let bottom = wrapper ? wrapper.getBoundingClientRect().bottom : 70;
        if (dock && window.matchMedia('(max-width: 989px)').matches) {
          const r = dock.getBoundingClientRect();
          if (r.height > 0) bottom = Math.max(bottom, r.bottom); // rect-based, not class-based
        }
        // floor: the search bar itself must stay visible above the panel
        const own = this.form ? this.form.getBoundingClientRect().bottom : 0;
        bottom = Math.max(bottom, own, 48);
        this.dropdown.style.setProperty('--cc-ov-top', Math.round(bottom) + 'px');
      };
      // OPENING (hidden not yet flipped): the sync measure() below no-ops
      // on its hidden guard, and the header may still be sliding in (the
      // search pill un-hides it, 300ms transform) — so the panel painted
      // at a stale top over the bar, then snapped when the 400ms re-measure
      // fired (Tony 2026-08-06 desktop). Measure every frame through the
      // settle window instead: first tick lands after hidden flips but
      // before paint, and the panel rides the bar to its resting spot.
      if (this.dropdown.hidden) {
        cancelAnimationFrame(this._settle);
        const t0 = performance.now();
        const settle = () => {
          measure();
          this._settle = performance.now() - t0 < 450 ? requestAnimationFrame(settle) : null;
        };
        this._settle = requestAnimationFrame(settle);
      }
      measure();
      // LIVE re-measure at ALL widths (Tony: desktop panel parked over
      // the bar after a layout shift — the one-shot measure went stale;
      // phones already had this). scroll-zeroing stays phone-only
      // inside measure().
      clearTimeout(this._rm);
      this._rm = setTimeout(measure, 400);
      if (window.visualViewport && !this._vvTrack) {
        this._vvTrack = measure;
        window.visualViewport.addEventListener('resize', this._vvTrack);
        window.visualViewport.addEventListener('scroll', this._vvTrack);
      }
      if (!this._wsTrack) {
        this._wsTrack = measure;
        window.addEventListener('scroll', this._wsTrack, { passive: true });
      }
      window.addEventListener('resize', this._vvTrack || measure, { passive: true });
    } else if (window.matchMedia('(max-width: 749px)').matches) {
      // mobile HERO (round 15): the BAR pins to the top of the VISUAL
      // viewport — iOS pans the layout viewport for the keyboard, so an
      // in-flow bar ended up above the screen no matter where the sheet
      // was anchored (Tony's "sah" screenshot: results, no input).
      // CSS: .cc-search--hero.cc-search--open is position:fixed at
      // top:var(--cc-vv-top); the sheet hangs from the bar's live bottom.
      const measure = () => {
        if (this.dropdown.hidden) return;
        // body is position:fixed here, so any document scroll is pure
        // keyboard pan — ZERO it instead of chasing it with offsets (the
        // bar "detached from the top" mid-gesture, Tony's screenshot);
        // the fixed body means this is visually free
        if (window.scrollY) window.scrollTo(0, 0);
        const vv = window.visualViewport;
        this.style.setProperty('--cc-vv-top', Math.round(vv ? vv.offsetTop : 0) + 'px');
        this.dropdown.style.setProperty('--cc-vv-h', Math.round(vv ? vv.height : window.innerHeight) + 'px');
        const own = this.form ? this.form.getBoundingClientRect().bottom : 56;
        this.dropdown.style.setProperty('--cc-ov-top', Math.round(Math.max(own, 48)) + 'px');
      };
      // provisional top so the first frame isn't anchored to the bar's
      // OLD in-flow position (the pin class lands later in open())
      this.dropdown.style.setProperty('--cc-ov-top', '64px');
      requestAnimationFrame(measure);
      clearTimeout(this._rm);
      this._rm = setTimeout(measure, 400); // after the keyboard pan settles
      if (window.visualViewport && !this._vvTrack) {
        this._vvTrack = measure;
        window.visualViewport.addEventListener('resize', this._vvTrack);
        window.visualViewport.addEventListener('scroll', this._vvTrack);
      }
      if (!this._wsTrack) {
        this._wsTrack = measure;
        window.addEventListener('scroll', this._wsTrack, { passive: true });
      }
      this.lockBody();
    }
    this.dropdown.hidden = false;
    this.classList.add('cc-search--open');
    document.body.classList.add('cc-search-page-dim');
    // page scroll locks while the overlay is up — scrolling behind it made
    // the measured top go stale (panel overlapped the bar / gapped away)
    if (this.isHeader) this.lockBody();
    this.input.setAttribute('aria-expanded', 'true');
    this.options = Array.from(this.dropdown.querySelectorAll('[role="option"], .cc-search__all'));
    this.active = -1;
    this._openedAt = Date.now(); // outside-click grace window anchor
  }

  /* pre-typing panel: popular searches + quick links from the template */
  openIdle() {
    if (!this.idleTpl) return;
    this.dropdown.innerHTML = '';
    this.dropdown.appendChild(this.idleTpl.content.cloneNode(true));
    this.patchPopular();
    this.open();
  }

  /* Popular searches = REAL 30-day top sellers, baked into the index as
     `pop` at regen (Tony 2026-07-14; ShopifyQL - Shopify exposes no
     literal search-term API). The template's hardcoded list renders
     first and stays as the offline/absent-field fallback; chips swap in
     place once the index arrives (same data-cc-term delegation). */
  patchPopular() {
    const apply = () => {
      const pop = this.index && Array.isArray(this.index.pop) ? this.index.pop : null;
      if (!pop || !pop.length) return;
      const row = this.dropdown.querySelector('.cc-search__idle .cc-search__chips');
      if (!row) return; // idle already replaced by results
      const icon = row.querySelector('.cc-search__chip svg');
      const svg = icon ? icon.outerHTML : '';
      row.innerHTML = '';
      pop.forEach((t) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cc-search__chip';
        btn.dataset.ccTerm = t;
        btn.setAttribute('role', 'option');
        btn.innerHTML = svg;
        btn.appendChild(document.createTextNode(t));
        row.appendChild(btn);
      });
    };
    if (this.index) apply();
    else this.loadIndex().then(apply);
  }

  async query() {
    const raw = this.input.value.trim();
    if (raw.length < 2) return this.openIdle();
    await this.loadIndex();
    if (!this.index) return;

    const tokens = CCSearch.fold(raw).split(/\s+/).filter(Boolean);
    const rank = (list) =>
      list
        .map((e) => [this.score(e, tokens), e])
        .filter(([sc]) => sc > 0)
        .sort((a, b) => b[0] - a[0])
        .map(([, e]) => e);

    // Hide rule (Tony 2026-07-11): products tagged POS are in-store only —
    // the index regen marks them x:1 (none tagged yet; DSP proved unreliable
    // and stays as an unused d:1 marker). In-stock results surface first.
    // NOTE 2026-07-19: products tagged cc-hide-search never enter the index
    // at all (regen excludes at build; main-search skips them server-side) —
    // the x filter stays as the belt for POS and any hand-flagged entries.
    const products = rank(this.index.products)
      .filter((p) => !p.x)
      .sort((a, b) => (b.av === 0 ? 0 : 1) - (a.av === 0 ? 0 : 1))
      .slice(0, 8);
    const cats = rank(this.index.collections).slice(0, 4);
    const lang = CCSearch.lang();
    const articles = rank(this.index.articles)
      .filter((a) => !a.tw || CCSearch.artLang(a.h) === lang)
      .slice(0, 3);
    this.render(raw, products, cats, articles);
    this.hydrateAvailability(products);
  }

  /* Live stock check (Tony 2026-07-13): the index av flag is a daily
     snapshot, so a product that sells out mid-day would still say
     "In stock" in the panel — Tony: never imply people can buy what
     they can't. Patch the visible cards from /products/<handle>.js
     (available = any variant purchasable), 5-min cache per handle.
     Label-only patch — no re-sort mid-view (jarring); the corrected
     av IS written back to the index entry, so the next keystroke's
     in-stock-first sort uses truth. Cards are looked up by href at
     resolve time: stale responses for replaced renders no-op. */
  hydrateAvailability(products) {
    if (!this._liveAv) this._liveAv = new Map();
    const TTL = 5 * 60 * 1000;
    const applyLive = (p, live) => {
      const avail = live.av;
      p.av = avail ? 1 : 0;
      /* 'from' pricing (Tony 2026-07-14, card parity): product.js knows
         price_varies + min price - written back to the index entry so the
         next keystroke renders it straight away */
      p.pv = !!live.pv;
      if (live.mp) p.p = live.mp;
      const card = this.dropdown.querySelector(`.cc-search__bigcard[href="/products/${p.h}"]`);
      if (!card) return;
      const oos = !avail;
      card.classList.toggle('cc-search__bigcard--oos', oos);
      const media = card.querySelector('.cc-search__bigcard-media');
      let ov = media && media.querySelector('.cc-search__oos');
      if (oos && media && !ov) {
        ov = document.createElement('span');
        ov.className = 'cc-search__oos';
        ov.textContent = 'Out of stock';
        media.appendChild(ov);
      } else if (!oos && ov) {
        ov.remove();
      }
      const priceEl = card.querySelector('.cc-search__bigcard-price');
      if (priceEl) priceEl.textContent = (p.pv ? 'from ' : '') + '$' + p.p;
      const line = card.querySelector('.cc-search__stock');
      if (line) {
        line.classList.toggle('cc-search__stock--out', oos);
        line.innerHTML = oos
          ? 'Out of stock'
          : 'In stock<span class="cc-search__stock-more"> • ready to ship</span>';
      }
    };
    products.forEach((p) => {
      const hit = this._liveAv.get(p.h);
      if (hit && Date.now() - hit.t < TTL) return applyLive(p, hit);
      fetch(`/products/${p.h}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!d || typeof d.available !== 'boolean') return;
          const live = {
            av: d.available,
            pv: !!d.price_varies,
            mp: typeof d.price === 'number' ? (d.price / 100).toFixed(2) : null,
            t: Date.now(),
          };
          this._liveAv.set(p.h, live);
          applyLive(p, live);
        })
        .catch(() => {}); // offline/hiccup: snapshot label stands
    });
  }

  /* term completions from the top matches, typed part bolded */
  suggestionsFor(raw, products, cats) {
    const seen = new Set([CCSearch.fold(raw)]);
    const out = [];
    const push = (t) => {
      const k = CCSearch.fold(t);
      if (!seen.has(k) && out.length < 5) {
        seen.add(k);
        out.push(t);
      }
    };
    cats.slice(0, 2).forEach((c) => push(c.t));
    products.forEach((p) => push(p.t));
    return out;
  }

  boldMatch(text, raw) {
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const i = CCSearch.fold(text).indexOf(CCSearch.fold(raw));
    if (i === -1) return esc(text);
    return (
      esc(text.slice(0, i)) +
      '<strong>' +
      esc(text.slice(i, i + raw.length)) +
      '</strong>' +
      esc(text.slice(i + raw.length))
    );
  }

  render(raw, products, cats, articles) {
    const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
    const big = (src) => (src ? src.replace('width=180', 'width=360') : src);

    // phones: the suggestions row is a single swipeable rail since
    // 2026-07-14 (was: 3-cap because wrapped rows shoved cards below the
    // fold - obsolete, the rail never wraps), so all 5 render everywhere
    const suggests = this.suggestionsFor(raw, products, cats)
      .map(
        (t) =>
          `<button type="button" class="cc-search__suggest" role="option" data-cc-term="${esc(t)}">${this.boldMatch(t, raw)}</button>`
      )
      .join('');

    const BADGES = { 1: ['Best Seller', 'bs'], 2: ['Trending', 'tr'], 3: ['New', 'new'] };
    const cards = products
      .map((p) => {
        const badge = BADGES[p.b];
        const oos = p.av === 0;
        const thumbs = p.v ? p.v.filter((v) => v.i) : [];
        const vrow =
          thumbs.length > 1
            ? `<span class="cc-search__vthumbs">${thumbs
                .slice(0, 4)
                .map((v) => `<img src="${esc(v.i)}" alt="${esc(v.t)}" title="${esc(v.t)}" loading="lazy" width="28" height="28">`)
                .join('')}${thumbs.length > 4 ? `<span class="cc-search__vmore">+${thumbs.length - 4}</span>` : ''}</span>`
            : '';
        return `<a class="cc-search__bigcard${oos ? ' cc-search__bigcard--oos' : ''}" href="/products/${esc(p.h)}" role="option">
          <span class="cc-search__bigcard-media">
            ${badge ? `<span class="cc-search__badge cc-search__badge--${badge[1]}">${badge[0]}</span>` : ''}
            ${p.i ? `<img src="${esc(big(p.i))}" alt="" loading="lazy" width="360" height="360">` : ''}
            ${oos ? '<span class="cc-search__oos">Out of stock</span>' : ''}
          </span>
          ${vrow}
          <span class="cc-search__bigcard-title">${esc(p.t)}</span>
          <span class="cc-search__bigcard-price">${p.pv ? 'from ' : ''}$${esc(p.p)}</span>
          <span class="cc-search__stock${oos ? ' cc-search__stock--out' : ''}">${oos ? 'Out of stock' : 'In stock<span class="cc-search__stock-more"> • ready to ship</span>'}</span>
        </a>`;
      })
      .join('');

    const catChips = cats.length
      ? `<div class="cc-search__chips cc-search__chips--cats">${cats
          .map(
            (c) =>
              `<a class="cc-search__chip cc-search__chip--link" href="/collections/${esc(c.h)}" role="option">${esc(c.t)}</a>`
          )
          .join('')}</div>`
      : '';

    /* blog results are mini article ROWS, not pills (Tony 2026-08-06):
       a chip built for two-word labels was holding full SEO headlines.
       Thumb comes straight from the index (a.i, 180w, nullable). */
    const artRows = articles.length
      ? `<p class="cc-search__group-label">From the blog</p><div class="cc-search__articles">${articles
          .map(
            (a) =>
              `<a class="cc-search__article" href="/blogs/${esc(a.h)}" role="option">` +
              `<span class="cc-search__article-thumb">${a.i ? `<img src="${esc(a.i)}" alt="" loading="lazy" width="56" height="56">` : ''}</span>` +
              `<span class="cc-search__article-title">${esc(a.t)}</span>` +
              `</a>`
          )
          .join('')}</div>`
      : '';

    // type=product,article — pages are excluded from /search (matches the form's hidden input)
    const allUrl = `/search?q=${encodeURIComponent(raw)}&type=product%2Carticle`;

    let html;
    if (products.length) {
      html = `<div class="cc-search__results">
        <aside class="cc-search__side">
          <p class="cc-search__group-label">Top suggestions</p>
          <div class="cc-search__suggests">${suggests}</div>
          ${catChips}
          ${artRows}
        </aside>
        <div class="cc-search__main">
          <div class="cc-search__cards">${cards}</div>
          <a class="cc-search__all" href="${allUrl}">See all results</a>
        </div>
      </div>`;
    } else {
      html = `<p class="cc-search__empty">Nothing found for “${esc(raw)}” — try another spelling.</p>${catChips}${artRows}<a class="cc-search__all" href="${allUrl}">Search the whole store</a>`;
    }

    this.dropdown.innerHTML = html;
    this.open();
  }

  onKey(e) {
    if (this.dropdown.hidden) return;
    if (e.key === 'Escape') return this.close();
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    if (e.key === 'Enter') {
      if (this.active >= 0) {
        e.preventDefault();
        this.options[this.active].click();
      }
      return; // no active option → form submits to /search
    }
    e.preventDefault();
    const dir = e.key === 'ArrowDown' ? 1 : -1;
    this.options[this.active]?.classList.remove('cc-search__option--active');
    this.active = (this.active + dir + this.options.length) % this.options.length;
    const el = this.options[this.active];
    el.classList.add('cc-search__option--active');
    el.scrollIntoView({ block: 'nearest' });
  }

  close() {
    // already closed? bail — TWO cc-search instances share the body
    // classes (header + hero); the header's outside-click close() was
    // stripping the hero's fresh scroll lock (iOS under-scroll bug)
    if (this.dropdown.hidden && !this.classList.contains('cc-search--open')) return;
    this.dropdown.hidden = true;
    if (this._settle) {
      cancelAnimationFrame(this._settle);
      this._settle = null;
    }
    if (this._vvTrack && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this._vvTrack);
      window.visualViewport.removeEventListener('scroll', this._vvTrack);
      this._vvTrack = null;
    }
    if (this._wsTrack) {
      window.removeEventListener('scroll', this._wsTrack);
      this._wsTrack = null;
    }
    this.style.removeProperty('--cc-vv-top');
    this.classList.remove('cc-search--open');
    document.body.classList.remove('cc-search-page-dim');
    document.body.classList.remove('cc-search-lock');
    document.body.classList.remove('cc-search-compact');
    if (this._lockY != null) {
      document.body.style.removeProperty('--cc-lock-top');
      window.scrollTo(0, this._lockY); // body just un-fixed — put the page back
      this._lockY = null;
    }
    this.input.setAttribute('aria-expanded', 'false');
    this.active = -1;
  }
}

customElements.define('cc-search', CCSearch);

// One page-dim layer, shown while any search dropdown is open
if (!document.querySelector('.cc-search-dim')) {
  const dim = document.createElement('div');
  dim.className = 'cc-search-dim';
  document.body.appendChild(dim);
}

/* /search server results (2026-07-19, Tony): Shopify returns BOTH halves
   of an ES<->EN article twin — hide the half that isn't the visitor's
   language, resolved against the index manifest's tw field (untwinned
   articles are never touched). Manifest-only fetch (8KB, no chunks).
   Known nuance: removed cards leave the result count/pagination very
   slightly off — same accepted trade as the page-result skip. Script is
   deferred, so the grid exists when this runs. */
(() => {
  if (!/\/search$/.test(location.pathname)) return;
  const grid = document.getElementById('product-grid');
  const src = document.querySelector('cc-search[data-index-url]');
  if (!grid || !src) return;
  const links = grid.querySelectorAll('a[href*="/blogs/"]');
  if (!links.length) return;
  fetch(src.dataset.indexUrl)
    .then((r) => r.json())
    .then((data) => {
      const lang = CCSearch.lang();
      const hide = new Set(
        ((data && data.articles) || [])
          .filter((a) => a.tw && CCSearch.artLang(a.h) !== lang)
          .map((a) => '/blogs/' + a.h)
      );
      if (!hide.size) return;
      links.forEach((a) => {
        const m = (a.getAttribute('href') || '').match(/\/blogs\/[^?#]+/);
        if (m && hide.has(m[0])) {
          const li = a.closest('li.grid__item');
          if (li) li.remove();
        }
      });
    })
    .catch(() => {}); /* offline: both halves show, nothing breaks */
})();
